import { api } from './api';

export type PeriodBucket = 'month' | 'quarter' | 'none';
export type PivotRateKind = 'period_end' | 'avg';

export interface PivotConfig {
  rowDims: string[];
  colDims: string[];
  periodStart: string;
  periodEnd: string;
  periodBucket: PeriodBucket;
  scenarios: string[];
  filters: Record<string, string[]>;
  reportingCurrency: string;
  normalizeCurrency: boolean;
  eliminateIntercompany: boolean;
  rootEntityId?: string;
  rateKind?: PivotRateKind;
  /** Org-change (組織変更) restatement: 'restated' remaps dims to current org structure. */
  orgChangeAsOf?: 'reported' | 'restated';
}

export interface PivotKey { values: string[]; labels: string[] }
export interface PivotCell { amount: number; factIds: string[]; sourceCurrencies: string[] }
export interface PivotMatrix {
  rowDims: string[];
  colDims: string[];
  rowKeys: PivotKey[];
  colKeys: PivotKey[];
  cells: Record<string, PivotCell>;
  totals: { rowTotals: number[]; colTotals: number[]; grandTotal: number };
  warnings: { mixedCurrencyCells: number; missingRates: { from: string; to: string }[]; truncated: boolean };
  normalized: boolean;
  reportingCurrency: string | null;
}

export interface PivotAxis { key: string; label: string; synthetic: boolean }
export interface SavedView {
  id: string;
  workspace_id: string;
  name: string;
  description: string | null;
  kind: string;
  config: PivotConfig;
  is_shared: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export const pivotApi = {
  compute: async (wsId: string, cfg: PivotConfig): Promise<PivotMatrix> =>
    (await api.post(`/api/workspaces/${wsId}/pivot`, cfg)).data,
  scenarios: async (wsId: string): Promise<string[]> =>
    (await api.get(`/api/workspaces/${wsId}/pivot/scenarios`)).data,
  axes: async (wsId: string): Promise<PivotAxis[]> =>
    (await api.get(`/api/workspaces/${wsId}/pivot/axes`)).data,
  views: {
    list: async (wsId: string): Promise<SavedView[]> =>
      (await api.get(`/api/workspaces/${wsId}/pivot/views`)).data,
    get: async (wsId: string, id: string): Promise<SavedView> =>
      (await api.get(`/api/workspaces/${wsId}/pivot/views/${id}`)).data,
    create: async (wsId: string, body: { name: string; description?: string; config: PivotConfig }): Promise<SavedView> =>
      (await api.post(`/api/workspaces/${wsId}/pivot/views`, body)).data,
    update: async (wsId: string, id: string, body: { name?: string; description?: string; config?: PivotConfig }): Promise<SavedView> =>
      (await api.patch(`/api/workspaces/${wsId}/pivot/views/${id}`, body)).data,
    remove: async (wsId: string, id: string): Promise<void> => { await api.delete(`/api/workspaces/${wsId}/pivot/views/${id}`); },
  },
};
